import React, { useState, useEffect, useRef } from 'react';
import FeedSearchInput from './FeedSearchInput';
import FeedFilterBar from './FeedFilterBar';

const SlidersIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.375 3.125V8.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4.375 12.5V16.875" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10 3.125V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10 8.75V16.875" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15.625 3.125V11.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15.625 15V16.875" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="4.375" cy="10.625" r="1.875" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="10" cy="6.875" r="1.875" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="15.625" cy="13.125" r="1.875" stroke="currentColor" strokeWidth="1.5" />
    </svg>
);

export default function FeedStickyHeader({
    scrollRef,
    query,
    onSubmit,
    onSelectSymbol,
    onSelectInsight,
    isVisible,
    symbols,
    onRemoveSymbol,
    onClearSymbols,
    // Filter props
    period, onPeriod,
    categories, category, onCategory,
    scope, onScope,
    unreadOnly, onToggleUnread,
    savedOnly, onToggleSaved,
}) {
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);
    const [isRevealed, setIsRevealed] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const headerRef = useRef(null);

    // Auto-reveal when searching or filtering starts
    useEffect(() => {
        if (query || category || period !== 'all' || scope !== 'all' || unreadOnly || savedOnly) {
            setIsRevealed(true);
        }
    }, [query, category, period, scope, unreadOnly, savedOnly]);

    // Handle scroll to hide
    useEffect(() => {
        const el = scrollRef?.current;
        if (!el || !isVisible) return;

        let lastScrollTop = el.scrollTop;
        const onScroll = () => {
            const currentScrollTop = el.scrollTop;
            const diff = currentScrollTop - lastScrollTop;
            
            // Show on scroll up, hide on scroll down (if not interacting)
            if (diff < -15) {
                // Scrolled up significantly
                setIsRevealed(true);
            } else if (diff > 15 && !isFocused && !isFiltersOpen) {
                // Scrolled down significantly
                setIsRevealed(false);
            }
            lastScrollTop = currentScrollTop;
        };

        el.addEventListener('scroll', onScroll);
        return () => el.removeEventListener('scroll', onScroll);
    }, [scrollRef, isVisible, isFocused, isFiltersOpen]);

    // Always show if visible and focused/interacting
    const activeReveal = isVisible && (isRevealed || isFocused || isFiltersOpen);

    return (
        <>
            {/* Trigger Zone: 60px invisible area at the top to detect mouse hover */}
            {isVisible && !activeReveal && (
                <div 
                    className="fixed top-0 left-0 right-0 h-[60px] z-40"
                    onMouseEnter={() => setIsRevealed(true)}
                />
            )}

            <div 
                ref={headerRef}
                className={`absolute top-0 left-0 right-0 bg-white border-b border-[#DEDEDE] z-30 transition-all duration-300 ease-in-out transform ${
                    activeReveal ? 'translate-y-0 opacity-100 shadow-sm' : '-translate-y-full opacity-0 pointer-events-none'
                }`}
                onMouseLeave={() => {
                    if (!isFocused && !isFiltersOpen) setIsRevealed(false);
                }}
            >
                <div className="flex flex-col max-w-[1472px] mx-auto px-[12px] md:px-[32px]">
                    <div className="h-[48px] flex items-center justify-between gap-[12px]">
                        <span className="text-[14px] font-bold text-[#111827] whitespace-nowrap">
                            Nebula Insights
                        </span>

                        <div className="flex items-center gap-[8px] shrink-0">
                            {symbols?.length > 0 && (
                                <div className="flex items-center gap-[6px] overflow-x-auto max-w-[320px] scrollbar-none">
                                    {symbols.map((sym) => (
                                        <span key={sym} className="inline-flex items-center gap-[4px] shrink-0 rounded-full bg-[#EEF2FF] text-[#4F46E5] px-[8px] py-[2px] text-[12px] font-medium">
                                            {sym}
                                            <button
                                                type="button"
                                                onClick={() => onRemoveSymbol?.(sym)}
                                                className="hover:text-[#312E81] flex items-center justify-center w-[14px] h-[14px] rounded-full hover:bg-[#E0E7FF] transition-colors"
                                                aria-label={`Xoá ${sym}`}
                                            >
                                                <svg width="8" height="8" viewBox="0 0 10 10" fill="none"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                                            </button>
                                        </span>
                                    ))}
                                    {symbols.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={onClearSymbols}
                                            className="shrink-0 text-[11px] text-[#9CA3AF] hover:text-[#111827] whitespace-nowrap transition-colors"
                                        >
                                            Xoá tất cả
                                        </button>
                                    )}
                                </div>
                            )}
                            <div className="w-[193px]">
                                <FeedSearchInput
                                    value={query}
                                    onSubmit={onSubmit}
                                    onSelectSymbol={onSelectSymbol}
                                    onSelectInsight={onSelectInsight}
                                    onFocus={() => setIsFocused(true)}
                                    onBlur={() => setIsFocused(false)}
                                />
                            </div>
                            <button 
                                type="button"
                                onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                                className={`w-[32px] h-[32px] flex items-center justify-center rounded-[8px] transition-all ${
                                    isFiltersOpen 
                                        ? 'text-[#4F46E5] bg-[#EEF2FF] border-[#C7D2FE]' 
                                        : 'text-[#636369] hover:text-[#111827] bg-white border-transparent'
                                } border`}
                            >
                                <SlidersIcon />
                            </button>
                        </div>
                    </div>

                    <div 
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            isFiltersOpen ? 'max-h-[200px] opacity-100 pb-[12px]' : 'max-h-0 opacity-0'
                        }`}
                    >
                        <FeedFilterBar
                            hideSearch={true}
                            period={period} onPeriod={onPeriod}
                            categories={categories} category={category} onCategory={onCategory}
                            scope={scope} onScope={onScope}
                            unreadOnly={unreadOnly} onToggleUnread={onToggleUnread}
                            savedOnly={savedOnly} onToggleSaved={onToggleSaved}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
